import { Suspense, useState, useEffect } from "react";
import { textureCache } from "../../services/textureCache";

function TexturePlane({ textureUrl, color }) {
  const [texture, setTexture] = useState(() => {
    // Check if texture is already cached
    return textureUrl ? textureCache.get(textureUrl) : null;
  });

  useEffect(() => {
    if (!textureUrl) {
      setTexture(null);
      return;
    }

    // Check if already cached synchronously
    const cached = textureCache.get(textureUrl);
    if (cached) {
      setTexture(cached);
      return;
    }

    // Load texture asynchronously
    let mounted = true;
    textureCache.load(textureUrl)
      .then((loadedTexture) => {
        if (mounted) {
          setTexture(loadedTexture);
        }
      })
      .catch((err) => {
        console.warn('Failed to load texture:', textureUrl, err);
        if (mounted) {
          setTexture(null);
        }
      });

    return () => {
      mounted = false;
    };
  }, [textureUrl]);

  // When texture exists, use it without color tinting and full opacity
  // When no texture, show the color
  return (
    <meshStandardMaterial
      map={texture || null}
      color={color}
      transparent={true}
      alphaTest={0.1}
      opacity={1.0}
      side={0}
    />
  );
}

function DecoratedPlane({ decor, edges = <></>, disableRaycast = false }) {
  const planeWidth = decor?.width;
  const planeHeight = decor?.height;

  const url = decor?.option?.url;
  const color = decor?.option?.color || "blue";

  const hasValidTexture = url && typeof url === "string" && url.trim() !== "";
  const raycastProps = disableRaycast ? { raycast: () => null } : {};

  return (
    <mesh {...raycastProps}>
      <planeGeometry args={[planeWidth, planeHeight]} />
      {hasValidTexture ? (
        <Suspense
          fallback={
            <meshStandardMaterial
              color="#cccccc"
              side={2}
              transparent
              opacity={0.7}
            />
          }
        >
          <TexturePlane textureUrl={url} color={color} />
        </Suspense>
      ) : (
        <meshStandardMaterial
          color={color}
          side={0}
          transparent
          opacity={0.8}
        />
      )}
      {edges}
    </mesh>
  );
}

export default DecoratedPlane;
